import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";
import { updateWebUser } from "../../api/webUserAPI";
import { useTranslation } from "react-i18next";

const initialFormData = {
  firstName: "",
  lastName: "",
  email: "",
  username: "",
  group1: "",
  group2: "",
  group3: "",
  roles: "",
  readOnlyAccess: 0,
  readWriteAccess: 0,
  enabled: true,
};

export default function EditUserDialog({ open, onClose, onSuccess, user }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        username: user.username || "",
        group1: user.group1 || "",
        group2: user.group2 || "",
        group3: user.group3 || "",
        roles: Array.isArray(user.roles)
          ? user.roles[0] || ""
          : user.roles || "",
        readOnlyAccess: user.readOnlyAccess || 0,
        readWriteAccess: user.readWriteAccess || 0,
        enabled: user.enabled !== undefined ? user.enabled : true,
      });
    }
  }, [user]);

  const handleClose = () => {
    setFormData(initialFormData);
    onClose();
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      if (!user) throw new Error("User not found");

      const dataToSend = {
        ...user,
        ...formData,
        roles: formData.roles ? [formData.roles] : [],
      };

      const response = await updateWebUser(dataToSend);
      if (response.returnCode === -1) {
        throw new Error(response.exMessage || "Update failed");
      }
      console.log("User updated successfully");
      handleClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("Error updating user:", error);
      alert(error.message || "Failed to update user");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{t("users.dialogs.edit.title")}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          <TextField
            label={t("users.fields.username")}
            value={formData.username}
            onChange={(e) => handleFormChange("username", e.target.value)}
            fullWidth
          />
          <TextField
            label={t("users.fields.firstName")}
            value={formData.firstName}
            onChange={(e) => handleFormChange("firstName", e.target.value)}
            fullWidth
          />
          <TextField
            label={t("users.fields.lastName")}
            value={formData.lastName}
            onChange={(e) => handleFormChange("lastName", e.target.value)}
            fullWidth
          />
          <TextField
            label={t("users.fields.email")}
            value={formData.email}
            onChange={(e) => handleFormChange("email", e.target.value)}
            fullWidth
            type="email"
          />
          <TextField
            label={t("users.fields.group1")}
            value={formData.group1}
            onChange={(e) => handleFormChange("group1", e.target.value)}
            fullWidth
          />
          <TextField
            label={t("users.fields.group2")}
            value={formData.group2}
            onChange={(e) => handleFormChange("group2", e.target.value)}
            fullWidth
          />
          <TextField
            label={t("users.fields.group3")}
            value={formData.group3}
            onChange={(e) => handleFormChange("group3", e.target.value)}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>{t("users.fields.roles")}</InputLabel>
            <Select
              value={formData.roles}
              onChange={(e) => handleFormChange("roles", e.target.value)}
            >
              <MenuItem value="user">{t("users.roles.user")}</MenuItem>
              <MenuItem value="admin">{t("users.roles.admin")}</MenuItem>
              <MenuItem value="serviceUser">
                {t("users.roles.serviceUser")}
              </MenuItem>
            </Select>
          </FormControl>
          <TextField
            label={t("users.fields.readOnlyAccess")}
            value={formData.readOnlyAccess}
            onChange={(e) =>
              handleFormChange("readOnlyAccess", parseInt(e.target.value) || 0)
            }
            fullWidth
            type="number"
          />
          <TextField
            label={t("users.fields.readWriteAccess")}
            value={formData.readWriteAccess}
            onChange={(e) =>
              handleFormChange("readWriteAccess", parseInt(e.target.value) || 0)
            }
            fullWidth
            type="number"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.enabled}
                onChange={(e) => handleFormChange("enabled", e.target.checked)}
              />
            }
            label={t("users.fields.enabled")}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t("common.cancel")}</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={20} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
